import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { format } from 'date-fns';
import Header from '../../components/Header';
import ModalStandalone, { IModalHandle } from '../../lib/ModalStandalone';
import LeaveModal from '../../components/LeaveModal/LeaveModal';
import LeaveStatusBadge from '../../components/LeaveStatusBadge/LeaveStatusBadge';
import LeaveTrackingModal from '../../components/LeaveTrackingModal/LeaveTrackingModal';
import { getNotifsAction } from '../../actions/notif';
import { cancelLeaveAction, getMyLeavesAction } from '../../actions/leave';
import { leaveTypeLabel } from '../../constants/leave';
import { ILeave } from '../../types/leave';

import styles from './MemberLeaves.module.scss';

const formatDate = (iso?: string) => {
  if (!iso) return '-';
  try {
    return format(new Date(iso), 'dd/MM/yyyy');
  } catch (e) {
    return '-';
  }
};

const MemberLeaves = () => {
  const dispatch = useDispatch();
  const modalRef = useRef<IModalHandle>(null);
  const trackingModalRef = useRef<IModalHandle>(null);
  const [trackingLeave, setTrackingLeave] = useState<ILeave | null>(null);
  const { leaves, isLoading } = useSelector((store: any) => store.leaveReducer);
  const { user } = useSelector((store: any) => store.authReducer);
  const memberLeavesEnabled = user?.structure?.manageLeaveRequests === true;

  useEffect(() => {
    if (!memberLeavesEnabled) return;
    getMyLeavesAction(dispatch as any);
    getNotifsAction(dispatch as any).catch(() => {});
  }, [dispatch, memberLeavesEnabled]);

  const openCreate = () => modalRef.current?.open();
  const closeCreate = () => modalRef.current?.close();

  const openTracking = (l: ILeave) => {
    setTrackingLeave(l);
    trackingModalRef.current?.open();
  };

  const closeTracking = () => {
    trackingModalRef.current?.close();
    setTrackingLeave(null);
  };

  const refresh = () => {
    if (!memberLeavesEnabled) return;
    getMyLeavesAction(dispatch as any);
    getNotifsAction(dispatch as any).catch(() => {});
  };

  const cancel = async (leaveId: string) => {
    if (!memberLeavesEnabled) return;
    await cancelLeaveAction(dispatch as any, leaveId);
    getNotifsAction(dispatch as any).catch(() => {});
  };

  if (!memberLeavesEnabled) {
    return <Navigate to="/member/home" replace />;
  }

  return (
    <div className={styles.container}>
      <Header name="OMADA" isProfile />
      <div className={styles.content}>
        <div className={styles.headerRow}>
          <h2>Demandes de congés</h2>
          <button type="button" onClick={openCreate}>
            Nouvelle demande
          </button>
        </div>

        <div className={styles.list}>
          {(!leaves || leaves.length === 0) && (
            <div className={styles.muted}>
              Aucune demande de congés.
            </div>
          )}

          {leaves?.map((l: ILeave) => (
            <div key={l._id} className={styles.card}>
              <div className={styles.row}>
                <div>
                  <strong>{formatDate(l.startDate)} → Retour le {formatDate(l.endDate)}</strong>
                </div>
                <LeaveStatusBadge status={l.status} />
              </div>
              <div className={styles.muted}>
                Type : {leaveTypeLabel(l.leaveType, l.leaveTypeComment)}
              </div>
              {l.status === 'rejected' && l.rejectionReason && (
                <div className={styles.muted}>
                  Motif: {l.rejectionReason}
                </div>
              )}
              <div className={styles.actions}>
                <button type="button" className={styles.trackLink} onClick={() => openTracking(l)}>
                  Voir le détail du suivi
                </button>
                {l.status === 'pending' && (
                  <button
                    type="button"
                    className={styles.cancelBtn}
                    disabled={isLoading?.includes('CANCEL_LEAVE')}
                    onClick={() => cancel(l._id)}
                  >
                    Annuler
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ModalStandalone ref={modalRef} minWidth={320}>
        <LeaveModal onClose={closeCreate} onCreated={refresh} />
      </ModalStandalone>

      <ModalStandalone
        ref={trackingModalRef}
        minWidth={640}
        closeCallBack={() => setTrackingLeave(null)}
      >
        {trackingLeave && (
          <LeaveTrackingModal leave={trackingLeave} onClose={closeTracking} />
        )}
      </ModalStandalone>
    </div>
  );
};

export default MemberLeaves;
